import React, { useState } from 'react';
import { X, Copy, Check, Download, Code2 } from 'lucide-react';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { generateHTMLCSS, generateSwiftUI, generateFlutter, generateReactNative, generateAndroid } from '../data/codeTemplates';
import { cn } from '../lib/utils';

const CodePanel = ({ isOpen, onClose, animationName, settings, initialTab = "html-css" }) => {
  const [copiedTab, setCopiedTab] = useState(null);
  const [minify, setMinify] = useState(false);
  const [autoprefix, setAutoprefix] = useState(true);
  const [activeTab, setActiveTab] = useState(initialTab);

  // Sync activeTab with initialTab when it changes or panel opens
  React.useEffect(() => {
    if (isOpen) setActiveTab(initialTab);
  }, [isOpen, initialTab]);

  const handleCopy = async (code, tab) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedTab(tab);
      setTimeout(() => setCopiedTab(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDownload = () => {
    const code = generateHTMLCSS(animationName, settings);
    const blob = new Blob([code], { type: 'text/css' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${animationName}.css`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const formatCode = (code) => {
    if (minify) {
      return code.replace(/\s+/g, ' ').replace(/\n/g, '').trim();
    }
    return code;
  };

  if (!isOpen) return null;

  const htmlCssCode = formatCode(generateHTMLCSS(animationName, settings));
  const swiftCode = generateSwiftUI(animationName, settings);
  const flutterCode = generateFlutter(animationName, settings);
  const rnCode = generateReactNative(animationName, settings);
  const androidCode = generateAndroid(animationName, settings);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-2 md:p-4">
      <div className="w-full max-w-5xl h-[95vh] md:h-auto md:max-h-[90vh] bg-[#0F172A] rounded-2xl border border-white/10 shadow-[0_0_50px_rgba(0,0,0,0.5)] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-white/5 bg-white/[0.02]">
          <div className="flex items-center gap-4">
            <div className="p-2 rounded-lg bg-violet-500/10 border border-violet-500/20">
              <Code2 className="w-5 h-5 text-violet-400" />
            </div>
            <div>
              <h2 className="text-lg font-black text-white uppercase tracking-tighter">System Export</h2>
              <p className="text-[10px] text-slate-500 font-bold uppercase tracking-[0.2em] mt-0.5">Ready for production integration</p>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            <span className="hidden xs:inline-block px-3 py-1 bg-violet-500/10 text-violet-400 text-[9px] md:text-[10px] font-black uppercase tracking-widest rounded-md border border-violet-500/20 max-w-[100px] truncate">
              {animationName}
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-slate-500 hover:text-white hover:bg-white/5 rounded-xl transition-all"
            >
              <X className="w-6 h-6" />
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <div className="px-4 md:px-8 pt-6 border-b border-white/5 bg-[#0F172A] overflow-x-auto no-scrollbar shrink-0">
            <TabsList className="bg-white/5 border border-white/5 h-10 md:h-12 p-1 gap-1 rounded-xl w-fit flex-nowrap">
              <TabsTrigger value="html-css" className="rounded-lg data-[state=active]:bg-violet-600 data-[state=active]:text-white text-[10px] font-black uppercase tracking-widest px-6 h-full transition-all">
                CSS / HTML
              </TabsTrigger>
              <TabsTrigger value="swiftui" className="rounded-lg data-[state=active]:bg-violet-600 data-[state=active]:text-white text-[10px] font-black uppercase tracking-widest px-6 h-full transition-all">
                SwiftUI
              </TabsTrigger>
              <TabsTrigger value="android" className="rounded-lg data-[state=active]:bg-violet-600 data-[state=active]:text-white text-[10px] font-black uppercase tracking-widest px-6 h-full transition-all">
                Android
              </TabsTrigger>
              <TabsTrigger value="flutter" className="rounded-lg data-[state=active]:bg-violet-600 data-[state=active]:text-white text-[10px] font-black uppercase tracking-widest px-6 h-full transition-all">
                Flutter
              </TabsTrigger>
              <TabsTrigger value="react-native" className="rounded-lg data-[state=active]:bg-violet-600 data-[state=active]:text-white text-[10px] font-black uppercase tracking-widest px-6 h-full transition-all">
                React Native
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Options for HTML/CSS */}
          {activeTab === 'html-css' && (
            <div className="px-4 md:px-8 py-4 flex flex-wrap items-center gap-4 md:gap-8 border-b border-white/5 bg-white/[0.01]">
              <div className="flex items-center gap-3">
                <Switch
                  id="minify"
                  checked={minify}
                  onCheckedChange={setMinify}
                  className="data-[state=checked]:bg-violet-500 h-5 w-9 [&_[role=switch-thumb]]:h-4 [&_[role=switch-thumb]]:w-4"
                />
                <Label htmlFor="minify" className="text-[10px] font-black uppercase tracking-widest text-slate-500 cursor-pointer">Minify Code</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  id="autoprefix"
                  checked={autoprefix}
                  onCheckedChange={setAutoprefix}
                  className="data-[state=checked]:bg-violet-500 h-5 w-9 [&_[role=switch-thumb]]:h-4 [&_[role=switch-thumb]]:w-4"
                />
                <Label htmlFor="autoprefix" className="text-[10px] font-black uppercase tracking-widest text-slate-500 cursor-pointer">Vendor Prefixes</Label>
              </div>
            </div>
          )}

          {/* Tab Contents */}
          <div className="flex-1 overflow-hidden flex flex-col">
            <TabsContent value="html-css" className="flex-1 min-h-0 m-0 p-0 focus-visible:ring-0 overflow-hidden">
              <CodeBlock
                code={htmlCssCode}
                language="html"
                onCopy={() => handleCopy(htmlCssCode, 'html-css')}
                copied={copiedTab === 'html-css'}
                onDownload={handleDownload}
              />
            </TabsContent>
            <TabsContent value="swiftui" className="flex-1 min-h-0 m-0 p-0 focus-visible:ring-0 overflow-hidden">
              <CodeBlock
                code={swiftCode}
                language="swift"
                onCopy={() => handleCopy(swiftCode, 'swiftui')}
                copied={copiedTab === 'swiftui'}
              />
            </TabsContent>
            <TabsContent value="android" className="flex-1 min-h-0 m-0 p-0 focus-visible:ring-0 overflow-hidden">
              <CodeBlock
                code={androidCode}
                language="kotlin"
                onCopy={() => handleCopy(androidCode, 'android')}
                copied={copiedTab === 'android'}
              />
            </TabsContent>
            <TabsContent value="flutter" className="flex-1 min-h-0 m-0 p-0 focus-visible:ring-0 overflow-hidden">
              <CodeBlock
                code={flutterCode}
                language="dart"
                onCopy={() => handleCopy(flutterCode, 'flutter')}
                copied={copiedTab === 'flutter'}
              />
            </TabsContent>
            <TabsContent value="react-native" className="flex-1 min-h-0 m-0 p-0 focus-visible:ring-0 overflow-hidden">
              <CodeBlock
                code={rnCode}
                language="javascript"
                onCopy={() => handleCopy(rnCode, 'react-native')}
                copied={copiedTab === 'react-native'}
              />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
};

const CodeBlock = ({ code, onCopy, copied, onDownload }) => {
  return (
    <div className="h-full relative flex flex-col overflow-hidden">
      {/* Action Bar (Always Visible) */}
      <div className="absolute top-4 right-4 md:top-6 md:right-6 flex items-center gap-2 md:gap-3 z-10">
        {onDownload && (
          <Button
            variant="secondary"
            size="sm"
            onClick={onDownload}
            className="bg-white/5 hover:bg-white/10 border-white/5 text-slate-300 h-8 md:h-9 rounded-xl backdrop-blur-md px-3 md:px-4 flex items-center gap-2"
          >
            <Download className="w-3.5 h-3.5 md:w-4 h-4" />
            <span className="text-[9px] md:text-[10px] font-black uppercase tracking-widest hidden xs:inline">Download</span>
          </Button>
        )}
        <Button
          variant="default"
          size="sm"
          onClick={onCopy}
          className={cn(
            "h-8 md:h-9 rounded-xl backdrop-blur-md px-3 md:px-4 flex items-center gap-2 transition-all duration-300",
            copied
              ? "bg-green-500 hover:bg-green-600 text-white"
              : "bg-violet-600 hover:bg-violet-500 text-white shadow-lg shadow-violet-500/20"
          )}
        >
          {copied ? (
            <>
              <Check className="w-3.5 h-3.5 md:w-4 h-4" />
              <span className="text-[9px] md:text-[10px] font-black uppercase tracking-widest">Done!</span>
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5 md:w-4 h-4" />
              <span className="text-[9px] md:text-[10px] font-black uppercase tracking-widest">Copy</span>
            </>
          )}
        </Button>
      </div>

      <div className="flex-1 overflow-auto p-4 md:p-10 bg-[#0F172A] selection:bg-violet-500/30 custom-scrollbar">
        <pre className="text-xs md:text-sm font-mono text-slate-300 leading-relaxed">
          <code>{code}</code>
        </pre>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.02);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(139, 92, 246, 0.1);
          border-radius: 10px;
          border: 2px solid #0F172A;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(139, 92, 246, 0.3);
        }
      `}} />
    </div>
  );
};

export default CodePanel;
